import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/styles";
import { Button, Container, Grid, Typography } from "@material-ui/core";
import { Helmet } from "react-helmet-async";
import { headRewrite } from "../function/headRewrite";

const useStyles = makeStyles((theme) => ({
  root: {
    background: "#eeeeee",
    color: "#111",
    minHeight: "100vh",
    margin: 0,
    padding: 0,
    fontFamily:
      '"Helvetica Neue", Arial, "Noto Sans JP", "Yu Gothic", sans-serif',
  },
  pageFrame: {
    background: "#eeeeee",
    [theme.breakpoints.down("sm")]: {
      borderLeft: "none",
      borderRight: "none",
    },
  },
  hero: {
    padding: theme.spacing(8, 0, 10),
    borderBottom: "1px solid #c8c8c8",
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(6, 0, 7),
    },
  },
  section: {
    padding: theme.spacing(10, 0),
    borderBottom: "1px solid #c8c8c8",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(7, 0),
    },
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
    fontSize: 12,
    letterSpacing: "0.12em",
    fontWeight: 700,
    textTransform: "uppercase",
  },
  labelLine: {
    flex: 1,
    height: 1,
    background: "#111",
    opacity: 0.7,
  },
  heroTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(30px, 4.8vw, 58px)",
    lineHeight: 1.18,
    letterSpacing: "-0.08em",
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
  heading: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(28px, 4vw, 48px)",
    lineHeight: 1.45,
    letterSpacing: "-0.05em",
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
  subHeading: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(22px, 2.8vw, 34px)",
    lineHeight: 1.55,
    letterSpacing: "-0.04em",
    fontWeight: 500,
    marginBottom: theme.spacing(2.5),
  },
  heroBody: {
    maxWidth: 670,
    fontSize: 15,
    lineHeight: 2.1,
    color: "#222",
    marginBottom: theme.spacing(4),
    whiteSpace: "pre-line",
  },
  body: {
    fontSize: 15,
    lineHeight: 2.1,
    color: "#222",
    whiteSpace: "pre-line",
  },
  noteBody: {
    fontSize: 14,
    lineHeight: 2,
    color: "#333",
    whiteSpace: "pre-line",
  },
  buttonRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(4),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      textAlign: "center",
    },
  },
  primaryButton: {
    background: "#111",
    color: "#eeeeee",
    border: "1px solid #111",
    borderRadius: 0,
    padding: theme.spacing(1.6, 3.5),
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      background: "#eeeeee",
      color: "#111",
      boxShadow: "none",
    },
  },
  secondaryButton: {
    background: "transparent",
    color: "#111",
    border: "1px solid #111",
    borderRadius: 0,
    padding: theme.spacing(1.6, 3.5),
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      background: "#111",
      color: "#eeeeee",
      boxShadow: "none",
    },
  },
  textLink: {
    color: "#111",
    textDecoration: "none",
    borderBottom: "1px solid #111",
    paddingBottom: 4,
    fontSize: 14,
    fontWeight: 700,
  },
  keywordLine: {
    borderTop: "1px solid #111",
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    fontSize: 13,
    color: "#333",
  },
  heroVisual: {
    position: "relative",
    minHeight: 540,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      minHeight: 380,
      marginTop: theme.spacing(6),
    },
  },
  heroCircle: {
    position: "absolute",
    right: -40,
    bottom: 22,
    width: 270,
    height: 270,
    border: "1px solid #111",
    borderRadius: "50%",
    opacity: 0.65,
    [theme.breakpoints.down("sm")]: {
      width: 180,
      height: 180,
      right: -60,
    },
  },
  heroSquare: {
    position: "absolute",
    left: 24,
    top: 44,
    width: 190,
    height: 190,
    border: "1px solid #111",
    opacity: 0.55,
    [theme.breakpoints.down("sm")]: {
      width: 120,
      height: 120,
      left: 0,
    },
  },
  heroNumber: {
    position: "absolute",
    top: 20,
    right: 0,
    writingMode: "vertical-rl",
    fontSize: 13,
    letterSpacing: "0.12em",
    fontWeight: 700,
  },
  heroMock: {
    position: "relative",
    width: "74%",
    maxWidth: 420,
    aspectRatio: "1 / 1",
    border: "1px solid #111",
    background: "rgba(255, 255, 255, 0.18)",
    overflow: "hidden",
    transform: "rotate(2deg)",
    [theme.breakpoints.down("sm")]: {
      width: "84%",
      maxWidth: 320,
    },
  },
  heroMockImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  heroMockLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
  },
  heroMockTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(24px, 3vw, 36px)",
    lineHeight: 1.5,
    letterSpacing: "-0.04em",
    fontWeight: 500,
  },
  thesisCard: {
    border: "1px solid #111",
    minHeight: 310,
    padding: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: "transparent",
    [theme.breakpoints.down("sm")]: {
      minHeight: "auto",
    },
  },
  thesisLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },
  thesisText: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(21px, 2.4vw, 30px)",
    lineHeight: 1.72,
    letterSpacing: "-0.03em",
    fontWeight: 500,
  },
  knowledgeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  knowledgeItem: {
    borderLeft: "1px solid #111",
    paddingLeft: theme.spacing(3),
  },
  knowledgeTitle: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.7,
    marginBottom: theme.spacing(1.5),
  },
  brandGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    borderTop: "1px solid #c8c8c8",
    borderLeft: "1px solid #c8c8c8",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  brandItem: {
    borderRight: "1px solid #c8c8c8",
    borderBottom: "1px solid #c8c8c8",
    padding: theme.spacing(2.8, 2.2),
    minHeight: 188,
  },
  brandTag: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(1.4),
  },
  brandTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 20,
    lineHeight: 1.5,
    letterSpacing: "-0.03em",
    marginBottom: theme.spacing(1.5),
  },
  listBlock: {
    borderTop: "1px solid #111",
  },
  listItem: {
    display: "grid",
    gridTemplateColumns: "72px 260px 1fr",
    gap: theme.spacing(3),
    padding: theme.spacing(3, 0),
    borderBottom: "1px solid #c8c8c8",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1.5),
    },
  },
  listIndex: {
    fontSize: 14,
    fontWeight: 700,
    color: "#555",
  },
  listTitle: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.6,
  },
  card: {
    border: "1px solid #111",
    minHeight: 310,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: "transparent",
    [theme.breakpoints.down("sm")]: {
      minHeight: 250,
    },
  },
  cardNumber: {
    fontSize: 24,
    marginBottom: theme.spacing(2),
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: "0.02em",
    marginBottom: theme.spacing(2),
  },
  smallRule: {
    width: 32,
    height: 1,
    background: "#111",
    marginBottom: theme.spacing(3),
  },
  quoteBlock: {
    border: "1px solid #111",
    padding: theme.spacing(5),
    position: "relative",
    background: "#111",
    color: "#eeeeee",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(4, 3),
    },
  },
  quoteMark: {
    fontFamily: '"Times New Roman", serif',
    fontSize: 72,
    lineHeight: 0.8,
    opacity: 0.75,
    marginBottom: theme.spacing(2),
  },
  quoteText: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(22px, 3vw, 36px)",
    lineHeight: 1.72,
    letterSpacing: "-0.04em",
    fontWeight: 500,
  },
  inventoryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(2.5),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  inventoryCard: {
    border: "1px solid #111",
    minHeight: 370,
    padding: theme.spacing(2.5),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  inventoryCardLink: {
    color: "inherit",
    textDecoration: "none",
    transition: "background 180ms ease, transform 180ms ease",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.36)",
      transform: "translateY(-2px)",
    },
  },
  inventoryImage: {
    aspectRatio: "1 / 1",
    width: "100%",
    border: "1px solid #c8c8c8",
    background: "rgba(255,255,255,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    fontSize: 11,
    lineHeight: 1.8,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2.5),
    padding: theme.spacing(2),
  },
  inventoryProductImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  inventoryMeta: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(1),
  },
  inventoryTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 20,
    lineHeight: 1.5,
    letterSpacing: "-0.03em",
    marginBottom: theme.spacing(1.5),
  },
  faqGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  faqItem: {
    border: "1px solid #111",
    padding: theme.spacing(3),
  },
  faqQuestion: {
    fontSize: 17,
    fontWeight: 700,
    lineHeight: 1.8,
    marginBottom: theme.spacing(1.5),
  },
  finalCta: {
    margin: theme.spacing(8, 0),
    border: "1px solid #111",
    padding: theme.spacing(7, 3),
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  },
  finalTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(30px, 4.8vw, 58px)",
    lineHeight: 1.35,
    letterSpacing: "-0.05em",
    marginBottom: theme.spacing(2),
  },
  stripe: {
    position: "absolute",
    right: 20,
    top: 30,
    bottom: 30,
    width: 48,
    background:
      "repeating-linear-gradient(90deg, transparent 0, transparent 7px, #111 8px, #111 9px)",
    opacity: 0.6,
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  indexHero: {
    padding: theme.spacing(8, 0, 10),
    borderBottom: "1px solid #c8c8c8",
  },
  indexIntro: {
    maxWidth: 760,
  },
  guideGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  guideCard: {
    border: "1px solid #111",
    minHeight: 320,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    color: "#111",
    textDecoration: "none",
    transition: "background 0.2s ease, color 0.2s ease",
    "&:hover": {
      background: "#111",
      color: "#eeeeee",
    },
  },
  guideCategory: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },
  guideCardTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 24,
    lineHeight: 1.55,
    letterSpacing: "-0.04em",
    marginBottom: theme.spacing(2),
  },
  guideCardBody: {
    fontSize: 14,
    lineHeight: 2,
  },
  guideArrow: {
    marginTop: theme.spacing(3),
    fontWeight: 700,
    fontSize: 14,
  },
  notFound: {
    minHeight: "70vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: theme.spacing(8, 0),
  },
}));

const guideArticles = [
  {
    slug: "first_sacai_experience",
    category: "BRAND ENTRY",
    title: "初めてのsacaiをレンタル体験",
    listDescription:
      "見慣れた服の構造をずらし、重ね、組み替える。sacaiを“着ること”から理解する入口ページ。",
    seo: {
      title:
        "初めてのsacaiをレンタル体験｜異素材ミックスを着て知るEnclopedia",
      description:
        "sacaiが気になるけれど、何から見ればいいか分からない方へ。異素材切替、ドッキング、レイヤード構造など、sacaiの魅力を解説し、Enclopediaでレンタルできるアーカイブへ案内します。",
      keyword:
        "初めてのsacai,sacai 初めて,sacai レンタル,sacai 着てみたい,sacai 異素材,sacai ドッキング,sacai アーカイブ,サカイ レンタル,enclopedia,アンクロペディア",
      canonicalPath: "/archive_guide/first_sacai_experience",
    },
    hero: {
      label: "FIRST SACAI EXPERIENCE",
      number: "02 / ENTRY",
      titleLines: ["初めての", "SACAIをレンタル体験。"],
      body:
        "一見すると日常着の延長に見えるのに、\n近づくと異なる素材、用途、服の構造が一着の中で共存している。\nsacaiを初めて知るなら、まずは“着ることで分かる違い”から入るのが最も自然です。",
      keywords: [
        "first experience",
        "hybrid",
        "layering",
        "sacai",
      ],
      visualLabel: "ARCHIVE EXPERIENCE",
      visualTitle: ["見慣れた服の中に、", "違う構造を発見する。"],
      image: "media/archive_fashion/archive_guide2.png",
      imageAlt: "初めてのsacaiをレンタル体験するためのアーカイブガイド画像",
      ctaLabel: "レンタルできるsacaiを見る",
      ctaHref: "https://www.enclopedia-official.com/brand/SACAI（サカイ）/?number=63",
    },
    thesis: {
      text:
        "sacaiは、見慣れた服を“混ぜる”ことで、日常着の見え方を更新するブランドです。",
      keywords: ["hybrid", "structure", "experience"],
    },
    sections: {
      whyThisPage: {
        label: "02 / WHY THIS PAGE?",
        titleLines: [
          "sacaiが気になる。",
          "でも、どこが特別なのか説明しづらい。",
        ],
        body:
          "sacaiの服は、写真だけを見ると“デザインが凝っている服”に見えることがあります。\nしかし実際には、ニットとシャツ、ブルゾンとテーラード、スカートとプリーツのように、異なる服の役割を一着の中で組み替えている。\nその面白さは、着たときの立体感や動きでより明確になります。",
      },
  
      brandOverview: {
        label: "03 / BRAND OVERVIEW",
        titleLines: [
          "異素材を重ねるのではなく、",
          "服の役割を組み替える。",
        ],
        body:
          "sacaiは、日常的で見慣れた服を出発点にしながら、異なる素材や構造を接続することで、まったく新しい輪郭を作るブランドです。\nシャツ、ニット、MA-1、トレンチ、プリーツなど、誰もが知る服の要素を用いながら、その“当たり前の見え方”を静かに変えていきます。",
        knowledgeItems: [
          {
            title: "ドッキングは、装飾ではなく設計。",
            text:
              "異素材の切替は単なるアクセントではありません。前身頃と後身頃、上半身と裾、表地と裏地の役割をずらすことで、服そのものの構造を再編集しています。",
          },
          {
            title: "正面と横、動いた瞬間で印象が変わる。",
            text:
              "sacaiは、静止画よりも身体に乗ったときの見え方が重要です。横から見たボリューム、歩いたときの揺れ、重なった素材の間に生まれる奥行きが魅力になります。",
          },
          {
            title: "定番服を、定番のまま終わらせない。",
            text:
              "ミリタリー、ワーク、ニット、シャツといった身近な服を用いながら、配置や重なりを変えることで、既視感のある服を新しいものとして見せます。",
          },
          {
            title: "初めてほど、“着てみる”意味がある。",
            text:
              "sacaiの服は、写真では細部の面白さに目が向きがちです。実際に着ると、構造が身体にどう作用するかまで理解できます。",
          },
        ],
      },
  
      lineGuide: {
        label: "04 / HOW TO SEE SACAI?",
        titleLines: [
          "sacaiを見るなら、",
          "まず4つの視点から。",
        ],
        items: [
          {
            tag: "ENTRY 01",
            title: "HYBRID CONSTRUCTION",
            text:
              "異なる服の構造を一着に接続する。sacaiを理解する最も基本的な視点です。",
          },
          {
            tag: "ENTRY 02",
            title: "MATERIAL CONTRAST",
            text:
              "ニットと布帛、ミリタリーとプリーツなど、素材の緊張感によって服の表情を作ります。",
          },
          {
            tag: "ENTRY 03",
            title: "VOLUME & LAYER",
            text:
              "一枚の服の中に、レイヤードしているような奥行きを生む。横姿や動きに差が出ます。",
          },
          {
            tag: "ENTRY 04",
            title: "COLLABORATION",
            text:
              "他ブランドとの協業でも、sacaiは単なるロゴ交換ではなく、相手の服を自らの構造へ翻訳します。",
          },
        ],
      },
  
      firstExperience: {
        label: "05 / FIRST EXPERIENCE",
        titleLines: ["初めて着ると、", "何が分かるのか。"],
        intro:
          "sacaiの魅力は、ディテールを読むことと、身体に乗せて構造を感じることの両方で立ち上がります。",
        items: [
          {
            index: "01",
            title: "異素材が切り替わる理由",
            text:
              "素材が違うから目立つのではなく、それぞれが異なる役割を持つことで、一着の中に複数の服の記憶が残ります。",
          },
          {
            index: "02",
            title: "静止画では見えない横顔と動き",
            text:
              "重なりや切替は、正面だけでは理解しきれません。横から見たときのふくらみ、歩いたときの揺れで、sacaiらしさが強く表れます。",
          },
          {
            index: "03",
            title: "日常着の安心感と、少しの違和感",
            text:
              "見慣れた服の要素を残しているから取り入れやすい。一方で、その構造は普通の服とは違う。この二重性がsacaiの入口になります。",
          },
        ],
      },
  
      quote: {
        textLines: [
          "見慣れた服ほど、",
          "sacaiの編集で新しく見える。",
        ],
      },
  
      whyRent: {
        label: "06 / WHY RENT?",
        titleLines: ["sacaiは、", "実物で理解が深まる。"],
        items: [
          {
            number: "01",
            title: "素材の厚みと落ち感が分かる",
            text:
              "異素材の組み合わせは、写真より実物で見たほうが差が明確です。重さ、張り、落ち感まで体験できます。",
          },
          {
            number: "02",
            title: "横姿とレイヤーの立ち上がりを確かめられる",
            text:
              "sacaiの魅力は、正面だけでは完結しません。横からのシルエットや動きの中で、構造がはっきり見えてきます。",
          },
          {
            number: "03",
            title: "購入前に生活との距離を測れる",
            text:
              "デザイン性が高い服ほど、自分の日常にどう入るかを試す価値があります。レンタルは、その判断材料になります。",
          },
        ],
      },
  
      inventory: {
        label: "07 / AVAILABLE ARCHIVE",
        titleLines: ["Enclopediaで", "体験できるsacai。"],
        body:
          "実在庫への導線は、“sacaiらしさを理解する続き”として設計します。まず知り、次に実物で確かめる流れを作ります。",
        items: [
          {
            meta: "sacai × WTAPS / 2025SS",
            title: "コラボレーションジャケット",
            text:
              "ワークやミリタリーの文脈を持つジャケットに、sacaiらしい構造のずれとレイヤード感を重ねた一着。",
            href: "https://www.enclopedia-official.com/product/079f1c35-7f8d-45e4-aca8-fb7da822e05b",
            image:
              "media/product/1773151184.3163898079f1c35-7f8d-45e4-aca8-fb7da822e05b.png",
          },
          {
            meta: "sacai",
            title: "異素材切替ワンピース",
            text:
              "異なる素材が同居することで、静止した状態と動いたときの見え方が変わるsacaiらしいアーカイブ。",
            href: "https://www.enclopedia-official.com/product/7d7cd289-9842-470a-a417-a43c1b7750b6",
            image:
              "media/product/1778919202.29588277d7cd289-9842-470a-a417-a43c1b7750b6.png",
          },
        ],
      },
  
      faq: {
        label: "08 / FAQ",
        titleLines: ["初めての人が、", "気になりやすいこと。"],
        items: [
          {
            question: "sacaiは、どのアイテムから見るのがおすすめですか？",
            answer:
              "初めてなら、ニット、スカート、ジャケットなど、日常着の形式を残しながら構造の違いが見えやすいアイテムがおすすめです。",
          },
          {
            question: "写真と実物で印象はかなり変わりますか？",
            answer:
              "変わります。特に異素材切替やレイヤード構造は、厚みや横からの見え方、動いたときの揺れで印象が大きく変化します。",
          },
          {
            question: "デザイン性が強くて、着こなせるか不安です。",
            answer:
              "sacaiは見慣れた服を土台にしているため、実際に着ると意外に日常へ落とし込みやすい一着も多いです。レンタルで確認するのが合理的です。",
          },
          {
            question: "レンタル後に購入もできますか？",
            answer:
              "対象アイテムは、レンタル体験を経たうえで購入相談へ進める導線を想定しています。",
          },
        ],
      },
  
      finalCta: {
        titleLines: [
          "初めてのsacaiを、",
          "レンタルから始める。",
        ],
        body:
          "見慣れた服が、構造によって別の表情を持つ。\nその面白さを、Enclopediaで実際に体験してください。",
        buttonLabel: "レンタルできるsacaiを見る",
        buttonHref: "https://www.enclopedia-official.com/brand/SACAI（サカイ）/?number=63",
      },
    },
  },
  {
    slug: "first_comme_des_garcons_experience",
    category: "BRAND ENTRY",
    title: "初めてのCOMME des GARÇONSをレンタル体験",
    listDescription:
      "難しそうに見えるギャルソンを、“買う前に着る”ことで知るための入口ページ。",
    seo: {
      title:
        "初めてのコムデギャルソンをレンタル体験｜気軽に着て知るEnclopedia",
      description:
        "COMME des GARÇONSが気になるけれど、何から見ればいいか分からない方へ。ブランドの魅力、主要ラインの違い、初めて着ると分かることを解説し、Enclopediaでレンタルできるコムデギャルソンのアーカイブへ案内します。",
      keyword:
        "初めてのコムデギャルソン,コムデギャルソン 初めて,コムデギャルソン どんなブランド,コムデギャルソン 着てみたい,コムデギャルソン レンタル,COMME des GARCONS レンタル,コムデギャルソン オムプリュス,enclopedia,アンクロペディア",
      canonicalPath: "/archive_guide/first_comme_des_garcons_experience",
    },
    hero: {
      label: "FIRST COMME DES GARÇONS EXPERIENCE",
      number: "01 / ENTRY",
      titleLines: ["初めての", "COMME des GARÇONSをレンタル体験。"],
      body:
        "難しそうに見えるコムデギャルソンを、\n“買う前に着る”ことで知るための入口ページです。\nブランドの魅力、ラインの違い、初めて着たときに分かることを整理しながら、Enclopediaで体験できるアーカイブへつなげます。",
      keywords: [
        "first experience",
        "archive",
        "wear before own",
        "comme des garçons",
      ],
      visualLabel: "ARCHIVE EXPERIENCE",
      visualTitle: ["気になっていた服を、", "まず着て理解する。"],
      image: "media/archive_fashion/archive_guide1.png",
      imageAlt: "初めてのCOMME des GARÇONSをレンタル体験するためのアーカイブガイド画像",
      ctaLabel: "レンタルできるコムデギャルソンを見る",
      ctaHref: "https://www.enclopedia-official.com/search/COMME%20des%20GARCONS/",
    },
    thesis: {
      text:
        "ギャルソンは、知ってから着る服ではなく、着ることで見え方が変わる服でもあります。",
      keywords: ["entry", "experience", "understanding"],
    },
    sections: {
      whyThisPage: {
        label: "02 / WHY THIS PAGE?",
        titleLines: [
          "コムデギャルソンが気になる。",
          "でも、何から見ればいいか分からない。",
        ],
        body:
          "COMME des GARÇONSは、服を“分かりやすく消費する”ブランドではありません。\nだからこそ、初めて触れる人には少し遠く見えることがあります。\nこのページでは、ブランドを難解な知識としてではなく、“着ることで理解が始まる文化”として整理します。",
      },
      brandOverview: {
        label: "03 / BRAND OVERVIEW",
        titleLines: ["“難しいブランド”ではなく、", "見方を変えるブランド。"],
        body:
          "コムデギャルソンは、既存の美しさや服の“普通”を問い直してきたブランドです。\n黒、非対称、解体、膨らみ、余白。\n目立つ装飾だけではなく、服の構造そのものを更新する姿勢に大きな特徴があります。",
        knowledgeItems: [
          {
            title: "美しさを、別の角度から見る。",
            text:
              "端正さだけではなく、歪みや余白、未完成に見える形の中にも価値を見出す。ギャルソンは“なぜ美しいのか”を服で問い続けます。",
          },
          {
            title: "服より先に、身体の見え方を変える。",
            text:
              "服が身体を整えるのではなく、服が身体の輪郭や存在感を組み替える。その感覚は、実際に着ると最も分かりやすく立ち上がります。",
          },
          {
            title: "アーカイブで評価される理由。",
            text:
              "一過性のトレンドではなく、コレクションごとに問いがあり、時間が経っても読み直せる。だから過去作品が資料として残ります。",
          },
          {
            title: "初心者ほど、“着てみる”意味がある。",
            text:
              "写真で難しく見える服ほど、身体に乗せると印象が変わることがあります。最初の理解をレンタルが補助します。",
          },
        ],
      },
      lineGuide: {
        label: "04 / WHICH LINE?",
        titleLines: [
          "“コムデギャルソン”は一つではない。",
          "初めてなら、入口の違いを知る。",
        ],
        items: [
          {
            tag: "ENTRY 01",
            title: "COMME des GARÇONS",
            text:
              "ブランドの中核。既存の美しさや服の“普通”を問い直す姿勢が最も濃く表れます。",
          },
          {
            tag: "ENTRY 02",
            title: "COMME des GARÇONS HOMME PLUS",
            text:
              "メンズにおける実験の中心。テーラリング、身体、装飾を大胆に再構成します。",
          },
          {
            tag: "ENTRY 03",
            title: "COMME des GARÇONS HOMME DEUX",
            text:
              "日本の背広,日常に近いスーツやジャケットの中で、ギャルソンらしい緊張感を体験しやすいラインです。",
          },
          {
            tag: "ENTRY 04",
            title: "COMME des GARÇONS SHIRT / COMME des GARÇONS COMME des GARÇONS",
            text:
              "より軽やかな入口。日常着に近い距離で、ギャルソンの発想に触れられます。",
          },
        ],
      },
      firstExperience: {
        label: "05 / FIRST EXPERIENCE",
        titleLines: ["初めて着ると、", "何が分かるのか。"],
        intro:
          "ギャルソンの魅力は、説明を読むだけでは完結しません。身体に乗せた瞬間に初めて分かる“服との距離感”があります。",
        items: [
          {
            index: "01",
            title: "“黒い服”の奥にある違い",
            text:
              "黒を基調にしていても、素材の厚み、切替、襟の角度、分量感によって印象は大きく変わります。ギャルソンは色よりも構造で服を記憶に残します。",
          },
          {
            index: "02",
            title: "身体を整えるより、見え方を更新する",
            text:
              "一般的な服が身体を美しく整えるために働くのに対し、ギャルソンは身体の輪郭や存在感そのものを問い直すことがあります。",
          },
          {
            index: "03",
            title: "違和感は、難しさではなく入口になる",
            text:
              "最初は“少し変わっている”と感じる服でも、その違和感がどこから来るかを知ると、見方が変わります。",
          },
        ],
      },
      quote: {
        textLines: [
          "所有の前に、体験する。",
          "そのほうが、ギャルソンはずっと近くなる。",
        ],
      },
      whyRent: {
        label: "06 / WHY RENT?",
        titleLines: ["初めてのギャルソンほど、", "レンタルと相性がいい。"],
        items: [
          {
            number: "01",
            title: "写真だけでは分からない",
            text:
              "サイズ表よりも、身体に乗せたときの重心や空気感で印象が変わる。まず着ることで、理解の解像度が上がります。",
          },
          {
            number: "02",
            title: "違和感が入口になる",
            text:
              "少し難しく見える服でも、その違和感がどこから来るかを知ると、魅力に変わります。",
          },
          {
            number: "03",
            title: "所有前の判断材料になる",
            text:
              "高額なアーカイブほど、素材感や生活との距離を確かめたうえで選ぶ体験が重要です。",
          },
        ],
      },
      inventory: {
        label: "07 / AVAILABLE ARCHIVE",
        titleLines: ["Enclopediaで", "体験できる一着。"],
        body:
          "実在庫への導線は、“ブランド説明の続き”として設計します。読むだけで終わらず、気になった服をそのまま体験へ進めます。",
        items: [
          {
            meta: "COMME des GARÇONS",
            title: "Archive Item 01",
            text:
              "初めてのコムデギャルソン体験につながる、実物で確かめたいアーカイブアイテム。",
            href: "https://www.enclopedia-official.com/product/ee262ae8-20a8-4065-b227-e5d392176a03",
            image:
              "media/product/1727876263.9000912ee262ae8-20a8-4065-b227-e5d392176a03.jpg",
          },
          {
            meta: "COMME des GARÇONS",
            title: "Archive Item 02",
            text:
              "写真だけでは分かりにくいシルエットや素材感を、レンタルで体験できる一着。",
            href: "https://www.enclopedia-official.com/product/6d4e73e7-6ae8-4bc1-bbac-69a855493a72",
            image:
              "media/product/1755779605.73427256d4e73e7-6ae8-4bc1-bbac-69a855493a72.jpg",
          },
          {
            meta: "COMME des GARÇONS",
            title: "Archive Item 03",
            text:
              "ブランドらしい違和感や構造を、最初の一着として試しやすいアーカイブ。",
            href: "https://www.enclopedia-official.com/product/25b45365-050d-4281-9ad0-536f21f0fc99",
            image:
              "media/product/1758033139.971845925b45365-050d-4281-9ad0-536f21f0fc99.jpg",
          },
          {
            meta: "COMME des GARÇONS",
            title: "Archive Item 04",
            text:
              "詳細ページで状態やサイズを確認しながら、レンタル体験へ進めるアーカイブ。",
            href: "https://www.enclopedia-official.com/product/23d8dac3-41aa-42d7-8d6e-1fa68ab20251",
            image: "media/product/1776929485.797350423d8dac3-41aa-42d7-8d6e-1fa68ab20251.png",
          },
        ],
      },
      faq: {
        label: "08 / FAQ",
        titleLines: ["初めての人が、", "気になりやすいこと。"],
        items: [
          {
            question: "初めてでも、いきなりレンタルして大丈夫ですか？",
            answer:
              "大丈夫です。Enclopediaは、アーカイブに詳しい人だけでなく、“気になっているが、まだ着たことがない人”の入口として設計しています。",
          },
          {
            question: "コムデギャルソンは難しそうで、自分に合うか不安です。",
            answer:
              "その不安こそ、体験が役立つ部分です。写真や寸法だけでは分からない相性を、まず着て確かめられます。",
          },
          {
            question: "どのラインから見るのがおすすめですか？",
            answer:
              "日常との距離が近いHOMME DEUXやSHIRT、より強い体験を求めるならHOMME PLUSがおすすめです。",
          },
          {
            question: "レンタル後に購入もできますか？",
            answer:
              "対象アイテムは、レンタル体験を経たうえで購入相談へ進める導線を想定しています。",
          },
        ],
      },
      finalCta: {
        titleLines: [
          "初めてのコムデギャルソンを、",
          "レンタルから始める。",
        ],
        body:
          "難しそうに見える服ほど、実際に着ることで距離が縮まります。\nEnclopediaで、気になっていた一着を体験してください。",
        buttonLabel: "レンタルできるコムデギャルソンを見る",
        buttonHref: "https://www.enclopedia-official.com/search/COMME%20des%20GARCONS/",
      },
    },
  },
];

function Seo({ article, canonicalUrl }) {
  const title = article.seo.title;
  const description = article.seo.description;
  const keyword = article.seo.keyword;
  const ogImage = article.hero.image
    ? `${process.env.REACT_APP_IMAGE_URL || ""}${article.hero.image}`
    : `${process.env.PUBLIC_URL || ""}/favicon.ico`;

  useEffect(() => {
    headRewrite(title, description, keyword);
  }, [title, description, keyword]);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keyword} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:site_name" content="Enclopedia" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: title,
          description,
          mainEntityOfPage: canonicalUrl,
          publisher: {
            "@type": "Organization",
            name: "Enclopedia",
          },
          about: [
            "アーカイブファッション",
            "ブランドガイド",
            article.title,
          ],
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: typeof window !== "undefined" ? window.location.origin : "",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Archive Guide",
              item: `${typeof window !== "undefined" ? window.location.origin : ""}/archive_guide`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: article.title,
              item: canonicalUrl,
            },
          ],
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: article.sections.faq.items.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.answer,
            },
          })),
        })}
      </script>
    </Helmet>
  );
}

function IndexSeo() {
  const title = "アーカイブガイド｜ブランドを深く知るEnclopedia";
  const description =
    "Enclopediaのアーカイブガイド一覧。ブランドの入口、初めての体験、アーカイブを楽しむための知識をまとめています。";
  const keyword =
    "アーカイブガイド,ブランドガイド,ファッションアーカイブ,コムデギャルソン 初めて,Enclopedia";
  const canonicalUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/archive_guide`;

  useEffect(() => {
    headRewrite(title, description, keyword);
  }, [title, description, keyword]);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keyword} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Enclopedia" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: title,
          description,
          url: canonicalUrl,
          isPartOf: {
            "@type": "WebSite",
            name: "Enclopedia",
            url: typeof window !== "undefined" ? window.location.origin : "",
          },
        })}
      </script>
    </Helmet>
  );
}

function ArchiveGuideIndex({ classes }) {
  return (
    <div className={classes.root}>
      <IndexSeo />
      <Container maxWidth="xl">
        <div className={classes.pageFrame}>
          <section className={classes.indexHero}>
            <div className={classes.indexIntro}>
              <div className={classes.labelRow}>
                <span>ARCHIVE GUIDE</span>
                <span className={classes.labelLine} />
              </div>
              <Typography variant="h1" component="h1" className={classes.heroTitle}>
                ブランドを、
                <br />
                深く知る入口。
              </Typography>
              <Typography component="p" className={classes.heroBody}>
                Enclopediaのアーカイブガイドでは、ブランドの背景、初めて着ると見えてくること、アーカイブの楽しみ方を記事として整理します。
              </Typography>
            </div>
          </section>

          <section className={classes.section}>
            <div className={classes.guideGrid}>
              {guideArticles.map((article) => (
                <a
                  href={`/archive_guide/${article.slug}`}
                  className={classes.guideCard}
                  key={article.slug}
                >
                  <div>
                    <div className={classes.guideCategory}>{article.category}</div>
                    <Typography component="h2" className={classes.guideCardTitle}>
                      {article.title}
                    </Typography>
                    <Typography component="p" className={classes.guideCardBody}>
                      {article.listDescription}
                    </Typography>
                  </div>
                  <div className={classes.guideArrow}>記事を読む →</div>
                </a>
              ))}
            </div>
          </section>
        </div>
      </Container>
    </div>
  );
}

function ArchiveGuideNotFound({ classes }) {
  return (
    <div className={classes.root}>
      <Container maxWidth="xl">
        <div className={classes.notFound}>
          <div>
            <Typography component="h1" className={classes.heading}>
              記事が見つかりませんでした。
            </Typography>
            <Button className={classes.primaryButton} component="a" href="/archive_guide">
              アーカイブガイド一覧へ戻る
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
}

function ArchiveGuideDetail({ classes, article }) {
  const canonicalUrl = `${typeof window !== "undefined" ? window.location.origin : ""}${article.seo.canonicalPath}`;
  const { hero, thesis, sections } = article;

  return (
    <div className={classes.root}>
      <Seo article={article} canonicalUrl={canonicalUrl} />
      <Container maxWidth="xl">
        <div className={classes.pageFrame}>
          <section className={classes.hero}>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={7}>
                <div className={classes.labelRow}>
                  <span>{hero.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography variant="h1" component="h1" className={classes.heroTitle}>
                  {hero.titleLines.map((line, index) => (
                    <React.Fragment key={line}>
                      {line}
                      {index < hero.titleLines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </Typography>
                <Typography component="p" className={classes.heroBody}>
                  {hero.body}
                </Typography>
                <div className={classes.buttonRow}>
                <Button
                  className={classes.primaryButton}
                  component="a"
                  href={hero.ctaHref || "https://www.enclopedia-official.com/"}
                >
                  {hero.ctaLabel || "レンタルできるアイテムを見る"}
                </Button>
                  <a href="#faq" className={classes.textLink}>
                    初めての人が気になりやすいこと
                  </a>
                </div>
                <div className={classes.keywordLine}>
                  {hero.keywords.map((keyword, index) => (
                    <React.Fragment key={keyword}>
                      <span>{keyword}</span>
                      {index < hero.keywords.length - 1 && <span>/</span>}
                    </React.Fragment>
                  ))}
                </div>
              </Grid>
              <Grid item xs={12} md={5}>
                <div className={classes.heroVisual}>
                  <div className={classes.heroCircle} />
                  <div className={classes.heroSquare} />
                  <div className={classes.heroNumber}>{hero.number}</div>
                  <div className={classes.heroMock}>
                    <img
                      src={`${process.env.REACT_APP_IMAGE_URL || ""}${hero.image}`}
                      alt={hero.imageAlt}
                      className={classes.heroMockImage}
                    />
                  </div>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <div className={classes.labelRow}>
                  <span>{sections.whyThisPage.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {sections.whyThisPage.titleLines.map((line, index) => (
                    <React.Fragment key={line}>
                      {line}
                      {index < sections.whyThisPage.titleLines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {sections.whyThisPage.body}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <div className={classes.thesisCard}>
                  <div>
                    <div className={classes.thesisLabel}>KEY THESIS</div>
                    <Typography component="p" className={classes.thesisText}>
                      {thesis.text}
                    </Typography>
                  </div>
                  <div className={classes.keywordLine}>
                    {thesis.keywords.map((keyword, index) => (
                      <React.Fragment key={keyword}>
                        <span>{keyword}</span>
                        {index < thesis.keywords.length - 1 && <span>/</span>}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={5}>
                <div className={classes.labelRow}>
                  <span>{sections.brandOverview.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {sections.brandOverview.titleLines.map((line, index) => (
                    <React.Fragment key={line}>
                      {line}
                      {index < sections.brandOverview.titleLines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {sections.brandOverview.body}
                </Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                <div className={classes.knowledgeGrid}>
                  {sections.brandOverview.knowledgeItems.map((item) => (
                    <div className={classes.knowledgeItem} key={item.title}>
                      <div className={classes.knowledgeTitle}>{item.title}</div>
                      <Typography component="p" className={classes.noteBody}>
                        {item.text}
                      </Typography>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>{sections.lineGuide.label}</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {sections.lineGuide.titleLines.map((line, index) => (
                <React.Fragment key={line}>
                  {line}
                  {index < sections.lineGuide.titleLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </Typography>
            <div className={classes.brandGrid}>
              {sections.lineGuide.items.map((item) => (
                <div className={classes.brandItem} key={item.title}>
                  <div className={classes.brandTag}>{item.tag}</div>
                  <Typography component="h3" className={classes.brandTitle}>
                    {item.title}
                  </Typography>
                  <Typography component="p" className={classes.noteBody}>
                    {item.text}
                  </Typography>
                </div>
              ))}
            </div>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={5}>
                <div className={classes.labelRow}>
                  <span>{sections.firstExperience.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {sections.firstExperience.titleLines.map((line, index) => (
                    <React.Fragment key={line}>
                      {line}
                      {index < sections.firstExperience.titleLines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {sections.firstExperience.intro}
                </Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                <div className={classes.listBlock}>
                  {sections.firstExperience.items.map((item) => (
                    <div className={classes.listItem} key={item.index}>
                      <div className={classes.listIndex}>{item.index}</div>
                      <div className={classes.listTitle}>{item.title}</div>
                      <Typography component="p" className={classes.noteBody}>
                        {item.text}
                      </Typography>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.quoteBlock}>
              <div className={classes.quoteMark}>“</div>
              <Typography component="p" className={classes.quoteText}>
                {sections.quote.textLines.map((line, index) => (
                  <React.Fragment key={line}>
                    {line}
                    {index < sections.quote.textLines.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </Typography>
            </div>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>{sections.whyRent.label}</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {sections.whyRent.titleLines.map((line, index) => (
                <React.Fragment key={line}>
                  {line}
                  {index < sections.whyRent.titleLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </Typography>
            <Grid container spacing={3}>
              {sections.whyRent.items.map((reason) => (
                <Grid item xs={12} md={4} key={reason.number}>
                  <div className={classes.card}>
                    <div>
                      <div className={classes.cardNumber}>{reason.number}</div>
                      <div className={classes.cardTitle}>{reason.title}</div>
                      <div className={classes.smallRule} />
                      <Typography component="p" className={classes.body}>
                        {reason.text}
                      </Typography>
                    </div>
                  </div>
                </Grid>
              ))}
            </Grid>
          </section>

          <section className={classes.section} id="archive-items">
            <Grid container spacing={6}>
              <Grid item xs={12} md={4}>
                <div className={classes.labelRow}>
                  <span>{sections.inventory.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {sections.inventory.titleLines.map((line, index) => (
                    <React.Fragment key={line}>
                      {line}
                      {index < sections.inventory.titleLines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {sections.inventory.body}
                </Typography>
              </Grid>
              <Grid item xs={12} md={8}>
                <div className={classes.inventoryGrid}>
                  {sections.inventory.items.map((item) => (
                    <a
                      className={`${classes.inventoryCard} ${classes.inventoryCardLink}`}
                      href={item.href}
                      key={item.href}
                    >
                      <div>
                        <div className={classes.inventoryImage}>
                          {item.image ? (
                            <img
                              src={`${process.env.REACT_APP_IMAGE_URL || ""}${item.image}`}
                              alt={item.title}
                              className={classes.inventoryProductImage}
                            />
                          ) : (
                            <>
                              Product image
                              <br />
                              coming soon
                            </>
                          )}
                        </div>
                        <div className={classes.inventoryMeta}>{item.meta}</div>
                        <Typography component="h3" className={classes.inventoryTitle}>
                          {item.title}
                        </Typography>
                        <Typography component="p" className={classes.noteBody}>
                          {item.text}
                        </Typography>
                      </div>
                      <div style={{ marginTop: 20 }}>
                        <span className={classes.textLink}>
                          このアイテムを見る
                        </span>
                      </div>
                    </a>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section} id="faq">
            <div className={classes.labelRow}>
              <span>{sections.faq.label}</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {sections.faq.titleLines.map((line, index) => (
                <React.Fragment key={line}>
                  {line}
                  {index < sections.faq.titleLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </Typography>
            <div className={classes.faqGrid}>
              {sections.faq.items.map((faq) => (
                <div className={classes.faqItem} key={faq.question}>
                  <div className={classes.faqQuestion}>{faq.question}</div>
                  <Typography component="p" className={classes.noteBody}>
                    {faq.answer}
                  </Typography>
                </div>
              ))}
            </div>
          </section>

          <div className={classes.finalCta}>
            <div className={classes.stripe} />
            <Typography component="h2" className={classes.finalTitle}>
              {sections.finalCta.titleLines.map((line, index) => (
                <React.Fragment key={line}>
                  {line}
                  {index < sections.finalCta.titleLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </Typography>
            <Typography component="p" className={classes.heroBody} style={{ margin: "0 auto 32px" }}>
              {sections.finalCta.body}
            </Typography>
            <Button
              className={classes.primaryButton}
              component="a"
              href={sections.finalCta.buttonHref}
            >
              {sections.finalCta.buttonLabel}
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
}

export default function ArchiveGuideScreen() {
  const classes = useStyles();
  const { slug } = useParams();

  if (!slug) {
    return <ArchiveGuideIndex classes={classes} />;
  }

  const article = guideArticles.find((item) => item.slug === slug);

  if (!article) {
    return <ArchiveGuideNotFound classes={classes} />;
  }

  return <ArchiveGuideDetail classes={classes} article={article} />;
}
